import React from 'react';
import { AlertCircle, RefreshCcw } from 'lucide-react';
import { VoteDetails } from './ProposalDiff';

const VotesPanel = ({ votes, votesLoading, votesError, circles, onRefreshVotes, onDecision }) => {
  const resolveVoteTimeoutDays = React.useCallback(
    (circleId) => {
      if (!circleId) return 3;
      const target = (circles || []).find((c) => String(c.id) === String(circleId));
      const reference = target?.parent_id
        ? (circles || []).find((c) => String(c.id) === String(target.parent_id)) || target
        : target;
      const raw = Number(reference?.vote_timeout_days || 0);
      if (Number.isNaN(raw) || raw < 1) return 3;
      return raw;
    },
    [circles]
  );

  const formatAutoAccept = React.useCallback(
    (vote) => {
      if (!vote?.created_at) return null;
      const created = new Date(vote.created_at);
      if (Number.isNaN(created.getTime())) return null;
      const timeoutDays = resolveVoteTimeoutDays(vote.circle_id);
      const autoAccept = new Date(created.getTime() + timeoutDays * 24 * 60 * 60 * 1000);
      if (Number.isNaN(autoAccept.getTime())) return null;
      return autoAccept.toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    [resolveVoteTimeoutDays]
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-full overflow-hidden flex flex-col">
    <div className="flex items-start justify-between mb-4">
      <div>
        <div className="flex items-center gap-2 text-sm text-indigo-700 font-semibold">Votes désynchronisés</div>
        <h2 className="text-xl font-bold text-slate-900">Mes votes à effectuer</h2>
        <p className="text-slate-500 text-sm">Acceptez ou refusez les propositions en attente.</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onRefreshVotes}
          className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
          title="Actualiser mes votes"
        >
          <RefreshCcw size={14} />
        </button>
      </div>
    </div>

    {votesError && (
      <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">
        <AlertCircle size={16} />
        {votesError}
      </div>
    )}

    <div className="flex-1 overflow-y-auto space-y-3">
      {votesLoading ? (
        <div className="text-center text-slate-400 text-sm">Chargement...</div>
      ) : votes.length === 0 ? (
        <div className="text-center text-slate-400 text-sm">Aucun vote en attente.</div>
      ) : (
        votes.map((vote) => (
          <div key={vote.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">{vote.tension_title || 'Proposition'}</p>
                {vote.circle_id && (
                  <p className="text-xs text-slate-500">
                    {circles.find((c) => c.id === vote.circle_id)?.name || `Cercle #${vote.circle_id}`}
                  </p>
                )}
                {vote.tension_summary && <p className="text-xs text-slate-600 mt-1">{vote.tension_summary}</p>}
                {vote.decision === 'pending' && (
                  (() => {
                    const autoAcceptLabel = formatAutoAccept(vote);
                    return autoAcceptLabel ? (
                      <p className="text-xs text-slate-500 mt-1">Auto-accept le {autoAcceptLabel}</p>
                    ) : null;
                  })()
                )}
                <VoteDetails vote={vote} circles={circles} />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onDecision(vote.id, 'reject')}
                  className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                >
                  Refuser
                </button>
                <button
                  onClick={() => onDecision(vote.id, 'accept')}
                  className="px-3 py-1.5 text-xs bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100"
                >
                  Accepter
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
  );
};

export default VotesPanel;
